'use client';
import { LogoRegister, LogoSlogan, LogoText } from '@/components/common';
import TranslateDropdown from '@/components/common/TranslateDropdown';
import AppFooter from '@/components/layouts/AppFooter';

import CommonNav from '@/components/layouts/CommonNav';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { showToast } from '@/lib/toast';
import { format } from 'date-fns';
import { Check } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { signIn, useSession } from 'next-auth/react';
import axios from 'axios';
import CommentSection from '@/components/common/CommentSection';
import { LogoLinks } from '@/components/common/smartguy-logo';

export default function BlogDetails({ blog }) {
  const router = useRouter();

  const { data: session, update } = useSession();

  const [copied, setCopied] = useState(false);

  const [showDisplayNameModal, setShowDisplayNameModal] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [pendingComment, setPendingComment] = useState(null);

  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!session) {
      signIn(undefined, { callbackUrl: window.location.href });
      return;
    }

    if (!session?.user?.displayName) {
      setPendingComment(commentText);
      setShowDisplayNameModal(true);
      return;
    }

    if (!commentText.trim()) {
      showToast('Please enter a comment', 'error');
      return;
    }

    setLoading(true);
    try {
      const token = session?.accessToken;
      const payload = {
        blog: blog._id,
        content: commentText,
      };

      const res = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/blog-comments`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res?.data) {
        setCommentText('');
        showToast('Comment posted successfully', 'success');
      }
    } catch (error) {
      showToast('Failed to post comment', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formSchema = z.object({
    displayName: z.string().min(2, { message: 'Display name must be at least 2 characters.' }),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { displayName: '' },
  });

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://www.smartguy.com/blogs/${blog?.slug}`)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`https://www.smartguy.com/blogs/${blog?.slug}`)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(`https://www.smartguy.com/blogs/${blog?.slug}`)}&text=${encodeURIComponent('Check this out!')}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://www.smartguy.com/blogs/${blog?.slug}`)}`,
    url: `https://www.smartguy.com/blogs/${blog?.slug}`,
  };

  const openShare = (url) => {
    window.open(url, '_blank', 'width=600,height=400');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLinks.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast('Link successfully copied', 'success');
    } catch (err) {}
  };

  return (
    <main className="flex h-screen w-full flex-col items-center overflow-y-auto bg-white p-2">
      <div className="mx-auto max-w-7xl p-2">
        <div className="h-20">
          <div className="relative flex w-full items-center justify-between">
            {/* Logo Centered */}
            <div className="flex flex-1 items-center justify-center">
              <div className="flex w-full flex-col items-center">
                <Link href="/" className="flex flex-col items-center justify-center">
                  <div className="flex items-center">
                    <LogoText className="text-3xl" />
                    <LogoRegister />
                  </div>
                  <LogoSlogan className="me-4 text-center text-[.7rem]" />
                </Link>

                <LogoLinks />
              </div>
            </div>

            {/* Back Button Right */}
            <div className="absolute right-4">
              <Button
                className="text-primary h-9 w-9 rounded-full bg-gray-200 hover:scale-105 hover:bg-gray-200"
                onClick={() => router.back()}
              >
                <ArrowLeft className="size-5" />
              </Button>
            </div>
          </div>
        </div>

        <div>
          <CommonNav />
        </div>
        <div className="flex justify-end">
          <TranslateDropdown />
        </div>

        <Card className="rounded-2xl border-none shadow-xl">
          {/* Blog Image */}
          <div className="overflow-hidden rounded-t-lg">
            <Image
              src={`${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/${blog.imageUrl}`}
              alt=""
              width={300}
              height={300}
              className="w-full object-contain"
            />
          </div>

          <CardContent className="px-6 md:p-10">
            {/* Category + Date */}
            <div className="mb-3 flex flex-col items-start justify-between gap-3 lg:flex-row lg:items-center">
              <div className="flex items-center gap-3">
                <Badge className="text-primary bg-gray-100 text-sm">
                  {blog?.blogCategory?.title}
                </Badge>
                <span className="text-muted-foreground text-sm">
                  {blog?.createdAt ? format(new Date(blog.createdAt), 'dd MMM, yyyy') : 'N/A'}
                </span>
              </div>

              <div className="me-2 flex flex-col items-start justify-end space-y-2 gap-x-3 lg:flex-row lg:items-center lg:space-y-0">
                <div className="">
                  <p className="text-[.75rem] text-black">
                    Share this now - someone’s life could change.
                  </p>
                </div>
                <div className="flex gap-x-3">
                  <div
                    onClick={() => openShare(`${shareLinks.facebook}`)}
                    className="relative flex size-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 transition-all duration-300 hover:scale-110 hover:shadow-md"
                  >
                    <Image
                      src="/assets/icons/facebook.svg"
                      alt="Facebook"
                      width={22}
                      height={22}
                      className="transition-transform duration-300"
                    />
                  </div>

                  <div
                    onClick={() => openShare(`${shareLinks.whatsapp}`)}
                    className="relative flex size-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 transition-all duration-300 hover:scale-110 hover:shadow-md"
                  >
                    <Image
                      src="/assets/icons/whatsapp.svg"
                      alt="whatsapp"
                      width={22}
                      height={22}
                      className="transition-transform duration-300"
                    />
                  </div>

                  <div
                    onClick={() => openShare(`${shareLinks.twitter}`)}
                    className="relative flex size-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 transition-all duration-300 hover:scale-110 hover:shadow-md"
                  >
                    <Image
                      src="/assets/icons/twitter.svg"
                      alt="twitter"
                      width={20}
                      height={20}
                      className="pt-1 transition-transform duration-300"
                    />
                  </div>

                  <div
                    onClick={() => openShare(`${shareLinks.linkedin}`)}
                    className="relative flex size-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 transition-all duration-300 hover:scale-110 hover:shadow-md"
                  >
                    <Image
                      src="/assets/icons/linkedin.svg"
                      alt="linkedin"
                      width={22}
                      height={22}
                      className="transition-transform duration-300"
                    />
                  </div>

                  <div
                    onClick={() => copyToClipboard()}
                    className="relative flex size-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 transition-all duration-300 hover:scale-110 hover:shadow-md"
                  >
                    {!copied && (
                      <Image
                        src="/assets/icons/share-link.svg"
                        alt="linkedin"
                        width={22}
                        height={22}
                        className="transition-transform duration-300"
                      />
                    )}
                    {copied && <Check className={`h-6 w-6 transition-transform duration-300`} />}
                  </div>
                </div>
              </div>
            </div>

            {/* Title */}
            <h1 className="mb-6 text-lg leading-tight font-bold sm:text-3xl md:text-5xl">
              {blog?.title}
            </h1>

            {/* Content */}
            <div className="prose prose-lg text-muted-foreground max-w-none">
              <div dangerouslySetInnerHTML={{ __html: blog.description }} />
            </div>
          </CardContent>
        </Card>
        {/* Comment Section */}

        <div className="mt-5">
          <CommentSection referenceId={blog._id} type="Blog" />
        </div>

        {/* <Card className="border-none shadow-2xl rounded-3xl overflow-hidden bg-gradient-to-br from-white to-gray-50/50 mt-5">
          <CardHeader className="px-6 md:px-10 py-3 bg-white/80 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl shadow-sm">
                <MessageSquare className="size-5 text-primary" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-xl md:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  Leave a Comment
                </CardTitle>
                <CardDescription className="text-sm text-gray-600">
                  Share your thoughts. Once approved, they will go live.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="px-6 md:px-10 ">
            {!session ? (
              <div className="flex flex-col items-start justify-start text-start  px-4">
                <div className="max-w-md space-y-4">
                  <p className="text-gray-700 text-lg">
                    <span className="block mt-2 font-semibold text-primary">
                      Sign in to share your thoughts
                    </span>
                  </p>
                  <Button
                    onClick={() => signIn(undefined, { callbackUrl: window.location.href })}
                    className="px-8 py-1 bg-primary text-white rounded-lg font-semibold 
                   hover:bg-primary/90 hover:shadow-lg 
                   transition-all duration-200 
                   focus:ring-2 focus:ring-primary/50 focus:outline-none"
                    aria-label="Sign in to leave a comment"
                  >
                    Sign In to Comment
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2.5">
                  <label
                    htmlFor="comment"
                    className="text-sm font-semibold text-gray-700 flex items-center gap-2"
                  >
                    Your Comment
                    <span className="text-destructive text-xs">*</span>
                  </label>
                  <div className="relative">
                    <Textarea
                      id="comment"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Share your perspective, ask a question, or add to the discussion..."
                      className="min-h-[140px] border-2 border-gray-200/80 focus:border-primary/50 focus:ring-4 focus:ring-primary/10 resize-none rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto px-10 h-10 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Send className="size-4 mr-2" />
                  {loading ? "Posting..." : "Post Comment"}
                </Button>
              </form>
            )}


            {blog.blog_comments?.length > 0 && (
              <>
                <Separator className="my-10" />

                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                        Comments
                      </h3>
                      <Badge
                        variant="secondary"
                        className="rounded-full px-3 py-1 bg-primary/10 text-primary font-semibold border-0"
                      >
                        {blog.blog_comments.length}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {blog.blog_comments.map((comment) => {
                      const user = comment.user || comment.professional;

                      return (
                        <div
                          key={comment._id}
                          className="group p-6 bg-white rounded-2xl border-2 border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
                        >
                          <div className="flex items-start gap-4">
                            <Avatar className="size-12 border-2 border-primary/20 shadow-md ring-2 ring-white">
                              {user?.avatarUrl ? (
                                <AvatarImage src={user.avatarUrl} alt={`${user?.firstName} ${user?.lastName}`} />
                              ) : null}
                              <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-white font-bold text-lg">
                                {user?.firstName && `${user.firstName.charAt(0).toUpperCase()}`}
                              </AvatarFallback>
                            </Avatar>

                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-bold text-gray-900 text-base">
                                    {user?.firstName || "N/A"}
                                  </h4>
                                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                                    <span className="inline-block size-1 rounded-full bg-primary/60" />
                                    {format(new Date(comment.createdAt), "MMM dd, yyyy")}
                                    <span className="text-gray-300">•</span>
                                    {format(new Date(comment.createdAt), "hh:mm a")}
                                  </p>
                                </div>
                              </div>

                              <p className="text-sm text-gray-700 leading-relaxed">
                                {comment.content}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                </div>
              </>
            )}
          </CardContent>
        </Card> */}
        <AppFooter />
      </div>

      {/* Display Name Modal */}
    </main>
  );
}
